/**
 * Backtest Configuration Form
 * Configure and run backtests
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '../trading/card'
import { Input, Select } from '../trading/input'
import { Badge } from '../trading/badge'
import { Play, RotateCcw, Settings, TrendingUp } from 'lucide-react'

interface BacktestFormProps {
  algorithm?: any
  onStart: (config: any) => void
}

const SYMBOLS = [
  { value: 'EUR/USD', label: 'EUR/USD' },
  { value: 'GBP/USD', label: 'GBP/USD' },
  { value: 'USD/JPY', label: 'USD/JPY' },
  { value: 'BTC/USD', label: 'BTC/USD' },
  { value: 'ETH/USD', label: 'ETH/USD' },
  { value: 'XAU/USD', label: 'XAU/USD (Gold)' },
]

const DATA_SOURCES = [
  { value: 'histodatasource', label: 'HistoData' },
  { value: 'dukascopy', label: 'Dukascopy' },
  { value: 'oanda', label: 'OANDA' },
  { value: 'custom', label: 'Custom Upload' },
]

const COMMISSION_MODELS = [
  { value: 'per-lot', label: 'Per Lot' },
  { value: 'per-share', label: 'Per Share' },
  { value: 'percent', label: 'Percentage' },
]

export function BacktestForm({ algorithm, onStart }: BacktestFormProps) {
  const [config, setConfig] = useState({
    algorithmId: algorithm?.id || '',
    name: `${algorithm?.name || 'Algorithm'} - ${new Date().toLocaleDateString()}`,
    symbol: algorithm?.symbol || 'EUR/USD',
    dataSource: 'histodatasource',
    startDate: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    initialCapital: 10000,
    commission: 7,
    commissionModel: 'per-lot',
    slippage: 1,
    spread: 1.5,
    leverage: 1,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onStart(config)
  }

  const updateConfig = (field: string, value: any) => {
    setConfig((prev) => ({ ...prev, [field]: value }))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <RotateCcw className="h-5 w-5" />
          Backtest Configuration
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Configuration */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Basic Configuration
            </h3>
            <Input
              label="Backtest Name"
              value={config.name}
              onChange={(e) => updateConfig('name', e.target.value)}
              required
            />
            <Select
              label="Symbol"
              options={SYMBOLS}
              value={config.symbol}
              onChange={(e) => updateConfig('symbol', e.target.value)}
              required
            />
            <Select
              label="Data Source"
              options={DATA_SOURCES}
              value={config.dataSource}
              onChange={(e) => updateConfig('dataSource', e.target.value)}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="date"
                value={config.startDate}
                onChange={(e) => updateConfig('startDate', e.target.value)}
                required
              />
              <Input
                label="End Date"
                type="date"
                value={config.endDate}
                onChange={(e) => updateConfig('endDate', e.target.value)}
                required
              />
            </div>
          </div>

          {/* Account Settings */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Account Settings
            </h3>
            <Input
              label="Initial Capital ($)"
              type="number"
              min="100"
              step="100"
              value={config.initialCapital}
              onChange={(e) => updateConfig('initialCapital', parseFloat(e.target.value))}
              helperText="Starting account balance"
            />
            <Input
              label="Leverage"
              type="number"
              min="1"
              max="100"
              value={config.leverage}
              onChange={(e) => updateConfig('leverage', parseInt(e.target.value))}
              helperText="1 = No leverage, 100 = 1:100"
            />
          </div>

          {/* Trading Costs */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Trading Costs
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Commission Model"
                options={COMMISSION_MODELS}
                value={config.commissionModel}
                onChange={(e) => updateConfig('commissionModel', e.target.value)}
              />
              <Input
                label="Commission"
                type="number"
                min="0"
                step="0.01"
                value={config.commission}
                onChange={(e) => updateConfig('commission', parseFloat(e.target.value))}
                helperText={`$${config.commission} ${config.commissionModel === 'per-lot' ? 'per lot' : config.commissionModel === 'percent' ? '%' : 'per share'}`}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Slippage (Pips)"
                type="number"
                min="0"
                step="0.1"
                value={config.slippage}
                onChange={(e) => updateConfig('slippage', parseFloat(e.target.value))}
                helperText="Expected slippage per trade"
              />
              <Input
                label="Spread (Pips)"
                type="number"
                min="0"
                step="0.1"
                value={config.spread}
                onChange={(e) => updateConfig('spread', parseFloat(e.target.value))}
                helperText="Average spread"
              />
            </div>
          </div>

          {/* Summary */}
          <div className="p-4 rounded-lg bg-muted border border-border space-y-2">
            <h3 className="text-sm font-semibold">Backtest Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Duration:</span>{' '}
                <span className="font-medium">
                  {new Date(config.endDate).getTime() - new Date(config.startDate).getTime() > 0
                    ? `${Math.ceil((new Date(config.endDate).getTime() - new Date(config.startDate).getTime()) / (1000 * 60 * 60 * 24))} days`
                    : 'Invalid dates'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Symbol:</span>{' '}
                <span className="font-medium">{config.symbol}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Initial Capital:</span>{' '}
                <span className="font-medium">${config.initialCapital.toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Leverage:</span>{' '}
                <span className="font-medium">1:{config.leverage}</span>
              </div>
            </div>
          </div>

          <Button type="submit" className="w-full" size="lg">
            <Play className="h-4 w-4 mr-2" />
            Run Backtest
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
